import { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import { apiUrl } from "../../environmentConfig";
import { getDownloadUrl, getPublicFileUrl } from "../../util/fileUrls";

async function fetchDocument(file) {
  const candidates = [];

  if (file?.preview_url?.startsWith("blob:")) {
    candidates.push(file.preview_url);
  }

  if (file?.file_id) {
    candidates.push(`${apiUrl}/file/stream/${file.file_id}`);
  }

  const publicUrl = getPublicFileUrl(file);
  if (publicUrl) {
    candidates.push(publicUrl);
  }

  const downloadUrl = getDownloadUrl(file);
  if (downloadUrl && downloadUrl !== publicUrl) {
    candidates.push(downloadUrl);
  }

  let lastError = null;

  for (const url of candidates) {
    try {
      const response = await fetch(url, {
        credentials: url.startsWith(apiUrl) ? "include" : "omit",
      });

      if (!response.ok) {
        lastError = new Error(`Failed to load document (${response.status})`);
        continue;
      }

      return response.blob();
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Failed to load document");
}

export default function DocxPreview({ file }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      if (container) {
        container.innerHTML = "";
      }

      try {
        const blob = await fetchDocument(file);
        if (cancelled || !containerRef.current) {
          return;
        }

        await renderAsync(blob, containerRef.current, null, {
          className: "docx-preview-content",
          inWrapper: true,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to preview document");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [file]);

  return (
    <div className="docx-preview-wrapper">
      {loading && <p className="docx-preview-status">Loading document...</p>}
      {error && !loading && (
        <div className="docx-preview-status docx-preview-error">
          <p>Could not preview this document.</p>
          <p>{error}</p>
        </div>
      )}
      <div
        ref={containerRef}
        className="docx-preview-container"
        style={{ display: loading || error ? "none" : "block" }}
      />
    </div>
  );
}
